import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Card } from '../ui/Card';
import { RateLimitPresets } from './RateLimitPresets';
import type { AdminRateLimitRule } from '../../types/admin';

const scopeOptions = [
  { value: 'global', label: 'Global' },
  { value: 'plan', label: 'Plan' },
  { value: 'campaign', label: 'Campaign' },
  { value: 'ip', label: 'IP' },
  { value: 'api_key', label: 'API Key' },
  { value: 'custom', label: 'Custom' },
];

const algorithmOptions = [
  { value: 'fixed_window', label: 'Fixed window' },
  { value: 'sliding_window', label: 'Sliding window' },
  { value: 'token_bucket', label: 'Token bucket' },
];

const responseModeOptions = [
  { value: 'json', label: 'JSON' },
  { value: 'redirect', label: 'Redirect' },
  { value: 'custom', label: 'Custom' },
];

interface RateLimitRuleEditorProps {
  rule: AdminRateLimitRule | null;
  onSave: (payload: Partial<AdminRateLimitRule>) => void;
  onDelete?: (rule: AdminRateLimitRule) => void;
  onReset?: () => void;
}

export const RateLimitRuleEditor: React.FC<RateLimitRuleEditorProps> = ({
  rule,
  onSave,
  onDelete,
  onReset,
}) => {
  const { t } = useTranslation('admin');
  const [form, setForm] = useState<Partial<AdminRateLimitRule>>({
    name: '',
    scope: 'global',
    scope_id: undefined,
    scope_value: '',
    requests_per_minute: undefined,
    requests_per_hour: undefined,
    requests_per_day: undefined,
    burst_size: undefined,
    concurrent_limit: undefined,
    algorithm: 'fixed_window',
    priority: 0,
    active: true,
    is_exempt: false,
    response_status: 429,
    response_message: '',
    response_mode: 'json',
    response_redirect_url: '',
    retry_after_override: undefined,
    metadata: undefined,
  });
  const [metadataText, setMetadataText] = useState('');

  useEffect(() => {
    if (rule) {
      setForm({
        ...rule,
        response_status: rule.response_status ?? 429,
        response_message: rule.response_message ?? '',
        response_mode: rule.response_mode ?? 'json',
        response_redirect_url: rule.response_redirect_url ?? '',
        burst_size: rule.burst_size ?? undefined,
        concurrent_limit: rule.concurrent_limit ?? undefined,
      });
      setMetadataText(rule.metadata ? JSON.stringify(rule.metadata, null, 2) : '');
      return;
    }

    setForm((prev) => ({
      ...prev,
      name: '',
      scope: 'global',
      scope_id: undefined,
      scope_value: '',
      requests_per_minute: undefined,
      requests_per_hour: undefined,
      requests_per_day: undefined,
      burst_size: undefined,
      concurrent_limit: undefined,
      algorithm: 'fixed_window',
      priority: 0,
      active: true,
      is_exempt: false,
      response_status: 429,
      response_message: '',
      response_mode: 'json',
      response_redirect_url: '',
      retry_after_override: undefined,
    }));
    setMetadataText('');
  }, [rule]);

  const handleChange = (field: keyof AdminRateLimitRule, value: string | number | boolean | null) => {
    setForm((prev) => ({
      ...prev,
      [field]: value === '' ? null : value,
    }));
  };

  const handleSave = () => {
    let parsedMetadata: Record<string, unknown> | undefined = undefined;
    if (metadataText.trim()) {
      try {
        parsedMetadata = JSON.parse(metadataText);
      } catch (error) {
        setMetadataText(metadataText);
        return;
      }
    }

    onSave({
      ...form,
      metadata: parsedMetadata,
    });
  };

  const applyPreset = (preset: { rpm: number | null; rph: number | null; rpd: number | null }) => {
    setForm((prev) => ({
      ...prev,
      requests_per_minute: preset.rpm ?? undefined,
      requests_per_hour: preset.rph ?? undefined,
      requests_per_day: preset.rpd ?? undefined,
    }));
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">{t('rate_limits.rule_editor.title')}</h3>
        {rule && (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onReset}>
              {t('rate_limits.rule_editor.new_rule')}
            </Button>
            <Button variant="danger" onClick={() => rule && onDelete?.(rule)}>
              {t('rate_limits.rule_editor.delete')}
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.rule_editor.name')}</label>
          <Input
            value={form.name ?? ''}
            onChange={(event) => handleChange('name', event.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.rule_editor.scope')}</label>
          <Select
            value={form.scope ?? 'global'}
            onChange={(event) => handleChange('scope', event.target.value)}
            options={scopeOptions}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.rule_editor.scope_id')}</label>
          <Input
            type="number"
            value={form.scope_id ?? ''}
            onChange={(event) => handleChange('scope_id', event.target.value ? Number(event.target.value) : null)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.rule_editor.scope_value')}</label>
          <Input
            value={form.scope_value ?? ''}
            onChange={(event) => handleChange('scope_value', event.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.rule_editor.requests_per_minute')}</label>
          <Input
            type="number"
            value={form.requests_per_minute ?? ''}
            onChange={(event) => handleChange('requests_per_minute', event.target.value ? Number(event.target.value) : null)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.rule_editor.requests_per_hour')}</label>
          <Input
            type="number"
            value={form.requests_per_hour ?? ''}
            onChange={(event) => handleChange('requests_per_hour', event.target.value ? Number(event.target.value) : null)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.rule_editor.requests_per_day')}</label>
          <Input
            type="number"
            value={form.requests_per_day ?? ''}
            onChange={(event) => handleChange('requests_per_day', event.target.value ? Number(event.target.value) : null)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.rule_editor.burst_size')}</label>
          <Input
            type="number"
            value={form.burst_size ?? ''}
            onChange={(event) => handleChange('burst_size', event.target.value ? Number(event.target.value) : null)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.rule_editor.concurrent_limit')}</label>
          <Input
            type="number"
            value={form.concurrent_limit ?? ''}
            onChange={(event) => handleChange('concurrent_limit', event.target.value ? Number(event.target.value) : null)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.rule_editor.algorithm')}</label>
          <Select
            value={form.algorithm ?? 'fixed_window'}
            onChange={(event) => handleChange('algorithm', event.target.value)}
            options={algorithmOptions}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.rule_editor.priority')}</label>
          <Input
            type="number"
            value={form.priority ?? 0}
            onChange={(event) => handleChange('priority', event.target.value ? Number(event.target.value) : 0)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.rule_editor.response_status')}</label>
          <Input
            type="number"
            value={form.response_status ?? 429}
            onChange={(event) => handleChange('response_status', event.target.value ? Number(event.target.value) : 429)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.rule_editor.response_message')}</label>
          <Input
            value={form.response_message ?? ''}
            onChange={(event) => handleChange('response_message', event.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.rule_editor.response_mode')}</label>
          <Select
            value={form.response_mode ?? 'json'}
            onChange={(event) => handleChange('response_mode', event.target.value)}
            options={responseModeOptions}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.rule_editor.response_redirect_url')}</label>
          <Input
            value={form.response_redirect_url ?? ''}
            onChange={(event) => handleChange('response_redirect_url', event.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('rate_limits.rule_editor.retry_after')}</label>
          <Input
            type="number"
            value={form.retry_after_override ?? ''}
            onChange={(event) => handleChange('retry_after_override', event.target.value ? Number(event.target.value) : null)}
          />
        </div>
      </div>

      <div className="mt-4 space-y-3">
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={Boolean(form.active)}
              onChange={(event) => handleChange('active', event.target.checked)}
            />
            {t('rate_limits.rule_editor.active')}
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={Boolean(form.is_exempt)}
              onChange={(event) => handleChange('is_exempt', event.target.checked)}
            />
            {t('rate_limits.rule_editor.exempt')}
          </label>
        </div>

        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">{t('rate_limits.rule_editor.presets')}</p>
          <RateLimitPresets onApply={applyPreset} />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 mb-1 block">
            {t('rate_limits.rule_editor.metadata')}
          </label>
          <textarea
            className="w-full min-h-[120px] rounded-lg border border-gray-200 px-3 py-2 text-sm"
            value={metadataText}
            onChange={(event) => setMetadataText(event.target.value)}
            placeholder='{"conditions": {"campaign_id": 12}}'
          />
        </div>
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button onClick={handleSave}>{t('rate_limits.rule_editor.save')}</Button>
      </div>
    </Card>
  );
};

export default RateLimitRuleEditor;
